import { useCallback, useMemo } from 'react';
import { Inbox, Send, FileText, AlertOctagon, Trash2, Archive, Folder } from 'lucide-react';
import {
  CommandDialog,
  CommandInput,
  CommandList,
  CommandEmpty,
  CommandGroup,
  CommandItem,
} from '@/components/ui/command';
import { useMoveToFolderStore } from '@/stores/moveToFolderStore';
import { useFolderStore } from '@/stores/folderStore';
import { executeEmailAction } from '@/stores/emailCommands';
import { resolveEmailContext } from '@/lib/emailContext';
import { inferUnifiedRole } from '@/lib/folderRoles';
import type { Folder as FolderType } from '@/types/email';
import type { StandardUnifiedRole } from '@/constants/mailbox';
import type { ComponentType } from 'react';

const roleIconMap: Record<StandardUnifiedRole, ComponentType<{ className?: string }>> = {
  inbox: Inbox,
  sent: Send,
  drafts: FileText,
  spam: AlertOctagon,
  trash: Trash2,
  archive: Archive,
};

const fallbackIconMap: Record<string, ComponentType<{ className?: string }>> = {
  inbox: Inbox,
  send: Send,
  'file-text': FileText,
  'alert-octagon': AlertOctagon,
  'trash-2': Trash2,
  archive: Archive,
};

function getFolderIcon(folder: FolderType): ComponentType<{ className?: string }> {
  const role = inferUnifiedRole(folder);
  if (role && roleIconMap[role]) return roleIconMap[role];
  if (folder.icon && fallbackIconMap[folder.icon]) return fallbackIconMap[folder.icon];
  return Folder;
}

export function FolderPickerDialog() {
  const isOpen = useMoveToFolderStore((s) => s.isOpen);
  const emailIds = useMoveToFolderStore((s) => s.emailIds);
  const contextHints = useMoveToFolderStore((s) => s.contextHints);
  const close = useMoveToFolderStore((s) => s.close);

  const accountFolderCache = useFolderStore((s) => s.accountFolderCache);

  // Resolve each target through the shared resolver — it looks across the full
  // email index, the open detail, the open thread, and digest members, so
  // thread/digest messages resolve instead of silently dropping out (D11).
  // Caller-supplied hints back-fill ids the resolver can't place (search
  // result rows live outside the store, N16).
  const emailContexts = useMemo(() => {
    if (!emailIds.length) return [];
    return emailIds
      .map((id) => {
        const ctx = resolveEmailContext(id);
        if (ctx) {
          return { id, accountId: ctx.accountId, folderId: ctx.folderId, email: ctx.email };
        }
        const hint = contextHints[id];
        if (hint?.accountId) {
          return { id, accountId: hint.accountId, folderId: hint.folderId, email: null };
        }
        return null;
      })
      .filter((ctx): ctx is NonNullable<typeof ctx> => ctx !== null);
  }, [emailIds, contextHints]);

  // Get the set of unique account IDs
  const accountIds = useMemo(() => {
    return [...new Set(emailContexts.map((ctx) => ctx.accountId).filter(Boolean))] as string[];
  }, [emailContexts]);

  // Build the folder list — union of all involved accounts' folders, deduped by name
  const availableFolders = useMemo(() => {
    if (!accountIds.length) return [];

    // Folder IDs that the selected emails are currently in
    const currentFolderIds = new Set(emailContexts.map((ctx) => ctx.folderId).filter(Boolean));

    const isSingleAccount = accountIds.length === 1;

    if (isSingleAccount) {
      // Single account — show that account's folders directly
      const folders = accountFolderCache[accountIds[0]] ?? [];
      return folders.filter((folder) => {
        if (currentFolderIds.has(folder.id)) return false;
        const role = inferUnifiedRole(folder);
        if (role === 'drafts' || role === 'sent') return false;
        return true;
      });
    }

    // Multi-account (unified mailbox) — build a deduped union by lowercase name.
    // For system folders this naturally merges (both accounts have "Inbox").
    // For custom folders that only exist in one account, we still show them —
    // the move handler will skip accounts that don't have a matching folder.
    const seen = new Map<string, FolderType>();
    for (const accId of accountIds) {
      for (const folder of (accountFolderCache[accId] ?? [])) {
        const key = folder.name.toLowerCase();
        if (!seen.has(key)) {
          seen.set(key, folder);
        }
      }
    }

    return Array.from(seen.values()).filter((folder) => {
      if (currentFolderIds.has(folder.id)) return false;
      const role = inferUnifiedRole(folder);
      if (role === 'drafts' || role === 'sent') return false;
      return true;
    });
  }, [accountIds, accountFolderCache, emailContexts]);

  const handleSelectFolder = useCallback(async (targetFolder: FolderType) => {
    close();

    if (!emailContexts.length) return;

    // The engine resolves the destination per account (so each email records
    // ITS OWN undo destination), applies optimistic removal, records undo only
    // after a successful dispatch, and restores + toasts on (partial) failure.
    // When every target shares one (account, folder) — the search-row case,
    // whose ids the engine can't resolve from the store — pass it as the
    // engine's explicit-context fallback (N16).
    const accountIdSet = new Set(emailContexts.map((ctx) => ctx.accountId));
    const folderIdSet = new Set(emailContexts.map((ctx) => ctx.folderId ?? ''));
    const uniform = accountIdSet.size === 1 && folderIdSet.size === 1 && emailContexts[0];
    await executeEmailAction(
      'move',
      { kind: 'messages', ids: emailContexts.map((ctx) => ctx.id) },
      {
        destination: { type: 'folder', folder: targetFolder, name: targetFolder.name },
        ...(uniform && emailContexts[0].folderId
          ? {
              contextAccountId: emailContexts[0].accountId,
              contextFolderId: emailContexts[0].folderId,
            }
          : {}),
      }
    );
  }, [emailContexts, close]);

  return (
    <CommandDialog
      open={isOpen}
      onOpenChange={(open) => { if (!open) close(); }}
      title="Move to folder"
      description="Select a destination folder"
      className="max-w-md h-fit"
      modal={false}
    >
      <CommandInput placeholder="Search folders..." />
      <CommandList className="max-h-[85vh] overflow-y-auto">
        <CommandEmpty>No folders found.</CommandEmpty>
        <CommandGroup heading="Folders">
          {availableFolders.map((folder) => {
            const Icon = getFolderIcon(folder);
            return (
              <CommandItem
                key={folder.id}
                value={folder.name}
                onSelect={() => void handleSelectFolder(folder)}
                className="gap-2"
              >
                <Icon className="h-4 w-4 text-muted-foreground" />
                <span>{folder.name}</span>
              </CommandItem>
            );
          })}
        </CommandGroup>
      </CommandList>
    </CommandDialog>
  );
}
